import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTrash, FaEye, FaPen } from "react-icons/fa";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000BasicDialog from "../../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000YesNoDialog from "../../../../u2000Core/components/dialog/U2000YesNoDialog";
import {
  useU3141GetDocumentsHandlers,
  useU3141DeleteDocumentHandlers,
  useU3141PatchDocumentNameHandlers,
} from "./U3141UseDocumentsHandlers";
import { useU3141UseViewDocumentHandlers } from "./U3141UseViewDocumentHandlers";
import { useU3141OpenDocumentPdfFileHandlers } from "./U3141UseOpenDocumentPdfFileHandlers";
import { U3141AddDocumentDialog } from "./U3141AddDocumentDialog";
import {
  doViewDocument,
  luHandleAppiError,
  U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG,
  type U3141LotDetailDocument,
  type U3141ChildLotChain,
  type U3141MasterLotChain,
} from "../../../U3141Service";
import { u3141IsNasTruthDocumentType } from "../../../tools/u3141OpenDocumentPdfFile";
import { useU3141Context } from "../../../U3141Context";
import useU2000AxiosAuthenticated from "../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { NS } from "../../../U3141";
import { u3141ResolveChildLotDocumentsForPanel } from "../../../tools/u3141DocumentAddDialogTypes";

export type U3141ChildLotChainDocumentsChangeDetail = {
  childLotChainId: string;
  documents: U3141LotDetailDocument[];
};

export interface U3141ChildLotChainDocumentsPanelProps {
  masterLotChainId: string;
  childLotChainId: string;
  childLotChain: U3141ChildLotChain | null;
  onDocumentsChange?: (detail?: U3141ChildLotChainDocumentsChangeDetail) => void;
  disableDocumentActions?: boolean;
  /** Empilement au-dessus du dialogue parent (ex. `z-120` si parent `z-110`). */
  nestedDialogOverlayClassName?: string;
}

type U3141DocumentRowIconActionProps = {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
};

function U3141DocumentRowIconAction({
  label,
  icon: Icon,
  onClick,
  disabled,
  destructive = false,
}: U3141DocumentRowIconActionProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${
        destructive ? "text-red-600 hover:bg-red-50" : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

export const U3141ChildLotChainDocumentsPanel: React.FC<U3141ChildLotChainDocumentsPanelProps> = ({
  masterLotChainId,
  childLotChainId,
  childLotChain,
  onDocumentsChange,
  disableDocumentActions = false,
  nestedDialogOverlayClassName,
}) => {
  const { t } = useTranslation();
  const { unitInitLU } = useU3141Context();
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const [documents, setDocuments] = useState<U3141LotDetailDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ doc: U3141LotDetailDocument } | null>(null);
  const [renameDoc, setRenameDoc] = useState<U3141LotDetailDocument | null>(null);
  const [renameNameDraft, setRenameNameDraft] = useState("");

  const notifyDocumentsChange = useCallback(
    (list: U3141LotDetailDocument[]) => {
      onDocumentsChange?.({ childLotChainId, documents: list });
    },
    [childLotChainId, onDocumentsChange],
  );

  const mergeDocumentsFromApi = useCallback(
    (apiList: U3141LotDetailDocument[]) =>
      u3141ResolveChildLotDocumentsForPanel(apiList, childLotChain?.lotDetail?.documentTypes),
    [childLotChain?.lotDetail?.documentTypes],
  );

  const { loadDocuments } = useU3141GetDocumentsHandlers({
    masterLotChainId,
    childLotChainId,
    onSuccess: setDocuments,
  });

  const onDeleteSuccess = useCallback(() => {
    void loadDocuments().then((list) => {
      const merged = mergeDocumentsFromApi(list);
      setDocuments(merged);
      notifyDocumentsChange(merged);
    });
  }, [loadDocuments, mergeDocumentsFromApi, notifyDocumentsChange]);

  const { deleteDocument } = useU3141DeleteDocumentHandlers({
    masterLotChainId,
    childLotChainId,
    onSuccess: onDeleteSuccess,
  });

  const onRenameSuccess = useCallback(() => {
    void loadDocuments().then((list) => {
      const merged = mergeDocumentsFromApi(list);
      setDocuments(merged);
      notifyDocumentsChange(merged);
    });
  }, [loadDocuments, mergeDocumentsFromApi, notifyDocumentsChange]);

  const { patchDocumentName } = useU3141PatchDocumentNameHandlers({
    masterLotChainId,
    childLotChainId,
    onSuccess: onRenameSuccess,
  });

  const handleViewDocumentError = useCallback(
    ({ error }: { error: unknown }) => {
      const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
    },
    [t, toast],
  );

  const { openDocumentPdfFile } = useU3141OpenDocumentPdfFileHandlers({
    axios,
    onError: handleViewDocumentError,
  });

  const { viewDocument } = useU3141UseViewDocumentHandlers({
    doViewDocumentFn: (documentId: string) => doViewDocument(axios, documentId),
    onSuccess: (row: { signUrl?: string } | unknown) => {
      const url = row && typeof row === "object" && row !== null && "signUrl" in row ? (row as { signUrl: string }).signUrl : null;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.error(t("errorNoDocumentUrl", { ns: NS }), t("errorTitle", { ns: NS }));
    },
    onError: handleViewDocumentError,
  });

  useEffect(() => {
    if (!masterLotChainId || !childLotChainId) return;
    setLoading(true);
    loadDocuments()
      .then((list) => setDocuments(mergeDocumentsFromApi(list)))
      .finally(() => setLoading(false));
  }, [masterLotChainId, childLotChainId, loadDocuments, mergeDocumentsFromApi]);

  useEffect(() => {
    if (renameDoc) {
      setRenameNameDraft(renameDoc.documentName ?? "");
    } else {
      setRenameNameDraft("");
    }
  }, [renameDoc]);

  const currentProject: U3141MasterLotChain | null = childLotChain?.masterLotChain
    ? {
        id: childLotChain.masterLotChain.id,
        projectNumber: childLotChain.masterLotChain.projectNumber ?? "",
        projectDescription: childLotChain.masterLotChain.projectDescription ?? "",
        requestedLot: "",
        createdAt: "",
        version: 0,
        childLotChains: [],
      }
    : null;

  const handleViewDocument = (doc: U3141LotDetailDocument) => {
    if (u3141IsNasTruthDocumentType(doc.documentType)) {
      void openDocumentPdfFile(doc.id);
      return;
    }
    viewDocument(doc.id);
  };

  const handleDeleteConfirm = (result: "yes" | "no") => {
    if (result === "yes" && deleteConfirm) {
      deleteDocument(deleteConfirm.doc.id);
    }
    setDeleteConfirm(null);
  };

  const handleRenameSave = () => {
    const trimmed = renameNameDraft.trim();
    if (!trimmed) {
      toast.error(t("errRenameDocumentNameRequired", { ns: NS }), t("errorTitle", { ns: NS }));
      return;
    }
    if (!renameDoc) return;
    void patchDocumentName(renameDoc.id, trimmed).then(() => {
      setRenameDoc(null);
    });
  };

  const getDocumentTypeLabel = (documentType?: string) => {
    if (!documentType) return "—";
    const key = `lblDocumentType.${documentType}`;
    const label = t(key, { ns: NS });
    return label !== key ? label : documentType;
  };

  const addableFromInit = useMemo(
    () =>
      unitInitLU.addableDocumentTypes?.length > 0 ? unitInitLU.addableDocumentTypes : [...U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG],
    [unitInitLU.addableDocumentTypes],
  );
  const canAddDocument = addableFromInit.length > 0;

  const renderDocumentRowActions = (doc: U3141LotDetailDocument) => (
    <div className="flex justify-end gap-0.5">
      <U3141DocumentRowIconAction
        label={t("btnViewDocument", { ns: NS })}
        icon={FaEye}
        onClick={() => handleViewDocument(doc)}
      />
      <U3141DocumentRowIconAction
        label={t("btnRenameDocument", { ns: NS })}
        icon={FaPen}
        onClick={() => setRenameDoc(doc)}
        disabled={disableDocumentActions}
      />
      <U3141DocumentRowIconAction
        label={t("btnDelete", { ns: NS })}
        icon={FaTrash}
        onClick={() => setDeleteConfirm({ doc })}
        disabled={disableDocumentActions}
        destructive
      />
    </div>
  );

  const tableBlock = loading ? (
    <p className="text-sm text-gray-500">{t("msgLoading", { ns: NS })}</p>
  ) : documents.length === 0 ? (
    <p className="text-sm text-gray-500">{t("msgNoDocuments", { ns: NS })}</p>
  ) : (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblDocumentType", { ns: NS })}
            </th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblDocumentName", { ns: NS })}
            </th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblActions", { ns: NS })}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {documents.map((doc) => (
            <tr key={doc.id} className="hover:bg-gray-50/80">
              <td className="px-4 py-3 text-sm font-medium text-gray-800">{getDocumentTypeLabel(doc.documentType)}</td>
              <td className="max-w-[240px] truncate px-4 py-3 text-sm text-gray-600" title={doc.documentName ?? undefined}>
                {doc.documentName ?? "—"}
              </td>
              <td className="px-4 py-2 text-right">{renderDocumentRowActions(doc)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="flex flex-col">
        <div className="flex items-center justify-end border-b border-gray-100 bg-gray-50 px-4 py-3">
          <U2000ActionBtn
            label={t("btnAddDocument", { ns: NS })}
            icon={FaPlus}
            handleClick={() => setAddDialogOpen(true)}
            disabled={disableDocumentActions || !canAddDocument}
          />
        </div>

        <div className="p-4">{tableBlock}</div>
      </div>

      <U3141AddDocumentDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => {
          void loadDocuments().then((list) => {
            const merged = mergeDocumentsFromApi(list);
            setDocuments(merged);
            notifyDocumentsChange(merged);
          });
        }}
        masterLotChainId={masterLotChainId}
        childLotChainId={childLotChainId}
        currentProject={currentProject}
        existingDocuments={documents}
        addableDocumentTypes={addableFromInit}
        singleSlotDocumentTypes={
          unitInitLU.singleSlotDocumentTypes?.length
            ? unitInitLU.singleSlotDocumentTypes
            : ['rfLotDetailIndex']
        }
        overlayClassName={nestedDialogOverlayClassName}
      />

      <U2000YesNoDialog
        isOpen={!!deleteConfirm}
        onClose={handleDeleteConfirm}
        title={t("dlgDeleteDocumentTitle", { ns: NS })}
        overlayClassName={nestedDialogOverlayClassName}
      >
        <p>{t("msgDeleteDocumentConfirm", { ns: NS })}</p>
      </U2000YesNoDialog>

      <U2000BasicDialog
        isOpen={!!renameDoc}
        onClose={() => setRenameDoc(null)}
        title={t("dlgRenameDocumentTitle", { ns: NS })}
        width="medium"
        overlayClassName={nestedDialogOverlayClassName}
      >
        <div className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="u3141-rename-document-name" className="mb-1 block text-sm font-medium text-gray-700">
              {t("lblDocumentName", { ns: NS })}
            </label>
            <input
              id="u3141-rename-document-name"
              type="text"
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              value={renameNameDraft}
              onChange={(e) => setRenameNameDraft(e.target.value)}
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50"
              onClick={() => setRenameDoc(null)}
            >
              {t("dlgLblCancel", { ns: NS })}
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              onClick={() => void handleRenameSave()}
            >
              {t("btnSaveRenameDocument", { ns: NS })}
            </button>
          </div>
        </div>
      </U2000BasicDialog>

    </>
  );
};
